import { Alert, Button, Modal, Skeleton, Typography } from "antd"
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"

import {
  type StoredPage,
  nextPageUrl
} from "../../../redux/shared/paged-list.ts"
import { SCROLL_PAGE_SIZE } from "../../../redux/shared/paged-list.ts"
import type { RootState } from "../../../store.ts"
import { extractParamsFromNextUrl } from "../../../utils/form/extract-params-from-next-url.ts"
import { type Lead } from "../../Lead/types/lead.types.ts"
import {
  approveLead,
  fetchLeadApproval,
  fetchProjectsFilterOptions
} from "../redux/projects.thunks.ts"
import { AssignProjectManagerModal } from "./AssignProjectManagerModal.tsx"
import LeadsApprovalList from "./LeadsApprovalList/LeadsApprovalList.tsx"
import LeadApprovalMobile from "./LeadsApprovalMobile/LeadsApprovalMobile.tsx"

const { Title } = Typography
const FIRST_PAGE = { page: 1, page_size: SCROLL_PAGE_SIZE }

interface ProjectsLeadApprovalProps extends ConnectedProps<typeof connector> {
  onClose: (refetch?: boolean) => void
}

function ProjectsLeadApproval({
  user,
  isMobile,
  onClose,
  fetchLeadApproval,
  approveLead,
  filterOptions,
  fetchFilterOptions,
  fetchingFilterOptions
}: ProjectsLeadApprovalProps) {
  const { t } = useTranslation()
  const [data, setData] = useState<StoredPage<Lead> | null>()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [firstPageFailed, setFirstPageFailed] = useState(false)

  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)

  const refetchRef = useRef<boolean>(false)

  const leads = data?.results || []

  /** `url` is the server's own `next`; absent, this is the first page. */
  const handleGetLeads = useCallback(
    async (url?: string | null) => {
      try {
        setLoading(true)

        const res = await fetchLeadApproval(
          url ? extractParamsFromNextUrl(url) : FIRST_PAGE
        ).unwrap()
        setData((current) =>
          url && current
            ? {
                ...res,
                results: [...current.results, ...res.results],
                retryNext: null
              }
            : { ...res, retryNext: null }
        )
        setFirstPageFailed(false)
        return true
      } catch {
        // The thunk already raised the error. What matters here is that `next`
        // stops pointing at the page that failed: it is what `hasMore` derives
        // from, and the scroll triggers re-fire on every settle while it
        // stands — against a failing endpoint that is an unbounded retry, one
        // error toast per round. The url moves to `retryNext`, which the list
        // footer offers back.
        setData((current) =>
          current ? { ...current, next: null, retryNext: url ?? null } : current
        )
        // `retryNext` can only carry a later page: on the first one there is no
        // stored page to hang it off, and an untouched `data` renders as a
        // successful empty queue.
        if (!url) setFirstPageFailed(true)
        return false
      } finally {
        setLoading(false)
      }
    },
    [fetchLeadApproval]
  )

  const handleLoadMore = () => {
    void handleGetLeads(nextPageUrl(data) ?? undefined)
  }

  const handleApproveLead = async (leadId: string, responsibleId: string) => {
    try {
      if (!data) return
      setSaving(true)

      await approveLead({ leadId, responsibleId }).unwrap()
      // From the first page: the approved lead leaves the list, so every page
      // after it shifts up by one.
      await handleGetLeads()

      if (!refetchRef?.current) refetchRef.current = true
    } catch (e) {
      console.error(e)
    } finally {
      setSaving(false)
    }
  }

  const handleSelectRow = (lead: Lead, action: string) => {
    switch (action) {
      case "approve":
        setSelectedLead(lead)
        setIsAssignModalOpen(true)
        break
    }
  }

  const handleAssignProjectManager = async (responsibleId: string) => {
    if (!selectedLead) return

    await handleApproveLead(selectedLead.id, responsibleId)

    setIsAssignModalOpen(false)
    setSelectedLead(null)
  }

  const handleCloseProjectManagerModal = () => {
    setIsAssignModalOpen(false)
    setSelectedLead(null)
  }

  useEffect(() => {
    void handleGetLeads()
    void fetchFilterOptions()
  }, [handleGetLeads, fetchFilterOptions])

  return (
    <>
      <Modal
        open
        title={
          <Title level={3} style={{ margin: 0, textAlign: "start" }}>
            {t("leadApproval.title")}
          </Title>
        }
        centered={true}
        footer={null}
        maskClosable={false}
        keyboard={false}
        onCancel={() => onClose(refetchRef?.current)}
        className="w-full lg:w-2/3 Select a responsible personxl:w-1/2"
      >
        <div
          className="overflow-y-auto flex flex-col size-full"
          style={{ maxHeight: "calc(100vh - 100px)" }}
        >
          {firstPageFailed ? (
            <Alert
              type="error"
              showIcon
              message={t("leadApproval.loadFailed")}
              action={
                <Button
                  size="small"
                  type="link"
                  loading={loading}
                  onClick={() => void handleGetLeads()}
                >
                  {t("leadApproval.retry")}
                </Button>
              }
            />
          ) : (
            <Suspense
              fallback={<Skeleton.Node active className="!w-full !h-[50lvh]" />}
            >
              {isMobile ? (
                <LeadApprovalMobile
                  leads={leads}
                  loading={loading || fetchingFilterOptions}
                  saving={saving}
                  hasMore={!!data?.next}
                  onLoadMore={handleLoadMore}
                  onActionClick={handleSelectRow}
                />
              ) : (
                <LeadsApprovalList
                  leads={leads}
                  saving={saving}
                  loading={loading || fetchingFilterOptions}
                  next={data?.next}
                  retryNext={data?.retryNext}
                  total={data?.count || 0}
                  onLoadMore={handleLoadMore}
                  onSelectRow={handleSelectRow}
                />
              )}
            </Suspense>
          )}
        </div>
      </Modal>

      {isAssignModalOpen && (
        <AssignProjectManagerModal
          projectManagers={filterOptions?.["project_manager"]}
          onConfirm={handleAssignProjectManager}
          onCancel={handleCloseProjectManagerModal}
          defaultSelectedId={user?.id}
          loading={saving}
        />
      )}
    </>
  )
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.userInfo,
  isMobile: state.nav.isMobile,
  fetchingFilterOptions: state.projects.fetchingFilterOptions,
  filterOptions: state.projects.filterOptions
})

const mapDispatchToProps = {
  fetchLeadApproval,
  approveLead,
  fetchFilterOptions: fetchProjectsFilterOptions
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProjectsLeadApproval)
